import React, { useEffect, useState, useRef } from "react";
import { FaBell, FaUserCircle, FaSignOutAlt } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import API from "../api";
import "bootstrap/dist/css/bootstrap.min.css";

import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllRead,
} from "../services/notificationService";

const timeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  const intervals = [
    { label: "year", seconds: 31536000 },
    { label: "month", seconds: 2592000 },
    { label: "day", seconds: 86400 },
    { label: "hr", seconds: 3600 },
    { label: "min", seconds: 60 },
  ];

  for (let i of intervals) {
    const count = Math.floor(seconds / i.seconds);
    if (count >= 1) return `${count} ${i.label}${count > 1 ? "s" : ""} ago`;
  }
  return "Just now";
};

const TopBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef();

  const [avatar, setAvatar] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userRole, setUserRole] = useState("");


  // Fetch avatar
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await API.get("/profile");
        setAvatar(data.avatar);
        setUserRole(data.role);
      } catch (err) {
        console.log("Avatar load failed");
      }
    };
    fetchProfile();
  }, []);

  // Fetch notifications
  useEffect(() => {
    loadNotifications();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const { data } = await fetchNotifications();
      const { data: countData } = await fetchUnreadCount();
      setNotifications(data);
      setUnreadCount(countData.count);
    } catch (err) {
      console.log("Notification load failed");
    }
  };

  const handleNotificationClick = async (n) => {
    if (!n.isRead) await markNotificationRead(n._id);
    setShowDropdown(false);
    navigate(n.link);
    loadNotifications();
  };

  const getPageTitle = () => {
    if (location.pathname === "/admin/dashboard") return "Admin Dashboard Overview";
    if (location.pathname === "/admin/AdminBlogs") return "Manage Blogs";
    if (location.pathname === "/admin/contact") return "Manage Contact Messages";
    if (location.pathname === "/admin/users") return "Manage Users";
    if (location.pathname === "/admin/contact-info") return "Contact Information";
    if (location.pathname === "/admin/profile") return "My Profile";
    if (location.pathname === "/admin/notifications") return "All Notifications (Latest 50) ";
    return "Admin Panel";
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/admin");
  };

  return (
    <div className="d-flex justify-content-between align-items-center bg-light p-3 rounded shadow-sm">
      <h4 className="m-0">{getPageTitle()}</h4>

      <div className="d-flex align-items-center gap-3">

        {/* 🔔 Notifications */}
        <div ref={dropdownRef} style={{ position: "relative", cursor: "pointer" }}>
          <FaBell
            size={20}
            title="Notifications"
            onClick={() => setShowDropdown(!showDropdown)}
            style={{
              color: showDropdown ? "#0d6efd" : "#555",
              transition: "color 0.2s",
            }}
          />

          {unreadCount > 0 && (
            <span
              style={{
                position: "absolute",
                top: -5,
                right: -8,
                background: "#dc3545",
                color: "white",
                borderRadius: "50%",
                fontSize: "10px",
                padding: "2px 6px",
                fontWeight: "600",
              }}
            >
              {unreadCount}
            </span>
          )}

          {showDropdown && (
            <div
              className="shadow"
              style={{
                position: "absolute",
                top: "35px",
                right: 0,
                width: "340px",
                background: "#fff",
                borderRadius: "10px",
                border: "1px solid #eee",
                zIndex: 999,
                maxHeight: "420px",
                overflowY: "auto",
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center p-2 border-bottom"
                style={{ position: "sticky", top: 0, background: "#fff", zIndex: 10 }}
              >
                <strong>Notifications</strong>

                <div className="d-flex gap-2">
                  {userRole === "superadmin" && (
                    <button
                      className="btn btn-sm text-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/admin/notifications");
                        setShowDropdown(false);
                      }}
                    >
                      See All
                    </button>
                  )}

                  <button
                    className="btn btn-sm text-primary"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await markAllRead();
                      loadNotifications();
                    }}
                  >
                    Mark all read
                  </button>
                </div>
              </div>


              {notifications.length === 0 && (
                <div className="p-3 text-center text-muted">No notifications</div>
              )}

              {notifications.map((n) => (
                <div
                  key={n._id}
                  onClick={() => handleNotificationClick(n)}
                  style={{
                    padding: "10px 12px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f1f1f1",
                    background: n.isRead ? "#fff" : "#f0f7ff",
                    borderLeft: n.isRead ? "4px solid transparent" : "4px solid #0d6efd",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#eef3f7")}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = n.isRead ? "#fff" : "#f0f7ff")
                  }
                >
                  <div className="d-flex justify-content-between">
                    <span style={{ fontWeight: n.isRead ? "500" : "600" }}>
                      {n.title}
                    </span>
                    {!n.isRead && (
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          background: "#0d6efd",
                          borderRadius: "50%",
                        }}
                      />
                    )}
                  </div>

                  <div style={{ fontSize: "13px", color: "#555" }}>{n.message}</div>
                  <div style={{ fontSize: "11px", color: "#999" }}>
                    {timeAgo(n.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 👤 Avatar */}
        <div onClick={() => navigate("/admin/profile")} title="My Profile">
          {avatar ? (
            <img
              src={`${process.env.REACT_APP_API_URL}/uploads/${avatar}`}
              alt="avatar"
              width="35"
              height="35"
              style={{ borderRadius: "50%", objectFit: "cover", cursor: "pointer" }}
            />
          ) : (
            <FaUserCircle size={28} className="text-secondary" />
          )}
        </div>

        {/* 🚪 Logout */}
        <button className="btn btn-danger btn-sm" onClick={handleLogout}>
          <FaSignOutAlt /> Logout
        </button>
      </div>
    </div>
  );
};

export default TopBar;
